import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  LinearProgress,
} from '@mui/material';
import {
  TableChart as TableChartIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import UserCourseDetailDialog from '../components/UserCourseDetailDialog';

interface ResultRow {
  nombre: string;
  apellido: string;
  sede: string;
  correo: string;
  departamento: string;
  empresa: string;
  curso: string;
  estado: string;
  porcentajeAvance: number;
  fechaInicio: string;
  fechaCompletado: string;
  puntuacion: number | string;
  userId: string;
  courseId: string;
  progressId: string | null;
}

const AdminAllResults: React.FC = () => {
  const { user } = useAuth();
  const [results, setResults] = useState<ResultRow[]>([]);
  const [filteredResults, setFilteredResults] = useState<ResultRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Array<{_id: string, name: string}>>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof ResultRow | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const tableScrollRef = React.useRef<HTMLDivElement>(null);
  const bottomScrollRef = React.useRef<HTMLDivElement>(null);
  const [userCourseDetailOpen, setUserCourseDetailOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ userId: string; userName: string; userEmail: string } | null>(null);

  // Cargar empresas al montar el componente
  useEffect(() => {
    loadCompanies();
  }, []);

  // Sincronizar scroll entre barra superior, tabla y barra inferior (fija)
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const tableScroll = tableScrollRef.current;
    const bottomScroll = bottomScrollRef.current;

    if (!topScroll || !tableScroll) return;

    const syncWidth = () => {
      const tableElement = tableScroll.querySelector('.MuiTable-root') as HTMLElement;
      const w = tableElement?.scrollWidth || 1400;
      const innerBox = topScroll.querySelector('div') as HTMLElement;
      const bottomBox = bottomScroll?.querySelector('div') as HTMLElement;
      if (innerBox) innerBox.style.minWidth = `${w}px`;
      if (bottomBox) bottomBox.style.minWidth = `${w}px`;
    };

    const syncScroll = (source: HTMLElement, targets: HTMLElement[]) => {
      targets.forEach(t => { if (t && t.scrollLeft !== source.scrollLeft) t.scrollLeft = source.scrollLeft; });
    };

    const targets = [tableScroll, bottomScroll].filter(Boolean) as HTMLElement[];
    const topTargets = [topScroll, bottomScroll].filter(Boolean) as HTMLElement[];
    const handleTopScroll = () => syncScroll(topScroll, targets);
    const handleTableScroll = () => syncScroll(tableScroll, topTargets);
    const handleBottomScroll = () => syncScroll(bottomScroll!, [topScroll, tableScroll]);

    syncWidth();
    const resizeObserver = new ResizeObserver(syncWidth);
    const tableElement = tableScroll.querySelector('.MuiTable-root');
    if (tableElement) resizeObserver.observe(tableElement);

    topScroll.addEventListener('scroll', handleTopScroll, { passive: true });
    tableScroll.addEventListener('scroll', handleTableScroll, { passive: true });
    bottomScroll?.addEventListener('scroll', handleBottomScroll, { passive: true });

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      bottomScroll?.removeEventListener('scroll', handleBottomScroll);
      resizeObserver.disconnect();
    };
  }, [results, filteredResults]);

  useEffect(() => {
    fetchResults();
  }, [selectedCompany]);

  useEffect(() => {
    filterAndSortResults();
  }, [searchTerm, statusFilter, departmentFilter, officeFilter, courseFilter, sortColumn, sortDirection, results]);

  const loadCompanies = async () => {
    try {
      const response = await api.getCompanies({ limit: 10000, page: 1 });
      const companiesList = response.companies.map((company: any) => ({
        _id: company._id,
        name: company.name
      }));
      companiesList.sort((a, b) => a.name.localeCompare(b.name));
      setCompanies(companiesList);
    } catch (err: any) {
      console.error('Error cargando empresas:', err);
      setCompanies([]);
    }
  };

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);
      const companyId = selectedCompany || undefined;
      const data = await api.getAllResults(companyId);
      setResults(data);
      setFilteredResults(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando resultados');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortResults = () => {
    let filtered = [...results];

    // Aplicar búsqueda de texto
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((row) =>
        row.nombre.toLowerCase().includes(searchLower) ||
        row.apellido.toLowerCase().includes(searchLower) ||
        row.correo.toLowerCase().includes(searchLower)
      );
    }

    // Aplicar filtro de estado (agrupa estados legacy con Pendiente / Iniciado / Completado)
    if (statusFilter) {
      const want = statusFilter.toLowerCase();
      filtered = filtered.filter(
        (row) => getStatusLabel(row.estado).toLowerCase() === want
      );
    }

    // Aplicar filtro de departamento
    if (departmentFilter) {
      filtered = filtered.filter((row) => row.departamento === departmentFilter);
    }

    // Aplicar filtro de sede
    if (officeFilter) {
      filtered = filtered.filter((row) => row.sede === officeFilter);
    }

    // Aplicar filtro de curso
    if (courseFilter) {
      filtered = filtered.filter((row) => row.curso === courseFilter);
    }

    // Aplicar ordenamiento
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue: any = a[sortColumn];
        let bValue: any = b[sortColumn];

        // Manejar valores especiales
        if (aValue === 'N/A' || aValue === null || aValue === '') aValue = null;
        if (bValue === 'N/A' || bValue === null || bValue === '') bValue = null;

        let comparison = 0;

        // Ordenamiento especial para fechas
        if (sortColumn === 'fechaInicio' || sortColumn === 'fechaCompletado') {
          if (aValue === null && bValue === null) {
            comparison = 0;
          } else if (aValue === null) {
            comparison = 1; // Los null van al final
          } else if (bValue === null) {
            comparison = -1;
          } else {
            const aDate = new Date(aValue).getTime();
            const bDate = new Date(bValue).getTime();
            comparison = aDate - bDate;
          }
        }
        // Ordenamiento especial para puntuación (número)
        else if (sortColumn === 'puntuacion') {
          const aNum = typeof aValue === 'number' ? aValue : (aValue === null ? -1 : 0);
          const bNum = typeof bValue === 'number' ? bValue : (bValue === null ? -1 : 0);
          comparison = aNum - bNum;
        }
        // Ordenamiento especial para porcentaje de avance (número)
        else if (sortColumn === 'porcentajeAvance') {
          const aNum = typeof aValue === 'number' ? aValue : 0;
          const bNum = typeof bValue === 'number' ? bValue : 0;
          comparison = aNum - bNum;
        }
        // Ordenamiento por string para el resto
        else {
          const aStr = String(aValue || '').toLowerCase();
          const bStr = String(bValue || '').toLowerCase();
          if (aStr < bStr) {
            comparison = -1;
          } else if (aStr > bStr) {
            comparison = 1;
          }
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredResults(filtered);
    setCurrentPage(1);
  };

  const handleSort = (column: keyof ResultRow) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completado':
        return 'success';
      case 'iniciado':
      case 'en progreso':
        return 'warning';
      case 'pendiente':
      case 'no iniciado':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completado':
        return 'Completado';
      case 'iniciado':
      case 'en progreso':
        return 'Iniciado';
      case 'pendiente':
      case 'no iniciado':
        return 'Pendiente';
      default:
        return status;
    }
  };

  // Obtener valores únicos para los filtros (una entrada por etiqueta)
  const uniqueStatuses = Array.from(
    new Set(
      results.map((r) => getStatusLabel(r.estado))
    )
  ).sort();
  
  // Cuando se selecciona una empresa, los resultados ya vienen filtrados del backend
  // Sin embargo, para asegurarnos de que solo mostramos departamentos y sedes de la empresa seleccionada,
  // filtramos por el nombre de la empresa también (por si acaso el backend no filtró correctamente)
  const resultsForFilters = selectedCompany 
    ? results.filter(r => {
        const selectedCompanyName = companies.find(c => c._id === selectedCompany)?.name;
        if (!selectedCompanyName) return false;
        // Comparación case-insensitive y sin espacios extra
        return r.empresa?.trim().toLowerCase() === selectedCompanyName.trim().toLowerCase();
      })
    : results;
  
  const uniqueDepartments = Array.from(new Set(resultsForFilters.map(r => r.departamento)))
    .filter(d => d !== 'N/A' && d !== null && d !== '' && d !== undefined)
    .sort();
  const uniqueOffices = Array.from(new Set(resultsForFilters.map(r => r.sede)))
    .filter(o => o !== 'N/A' && o !== null && o !== '' && o !== undefined)
    .sort();
  const uniqueCourses = Array.from(new Set(results.map(r => r.curso)))
    .sort();
  const uniqueCompanies = Array.from(new Set(results.map(r => r.empresa)))
    .filter(c => c !== 'N/A')
    .sort();

  const handleDownloadCSV = () => {
    // Preparar datos para CSV
    const csvHeaders = [
      'Nombre',
      'Apellido',
      'Empresa',
      'Sede',
      'Correo',
      'Departamento',
      'Curso',
      'Estado',
      'Porcentaje de Avance',
      'Fecha y Hora de Inicio',
      'Fecha y Hora de Completado',
      'Puntuación'
    ];

    const csvRows = filteredResults.map((row) => [
      row.nombre,
      row.apellido,
      row.empresa,
      row.sede,
      row.correo,
      row.departamento,
      row.curso,
      getStatusLabel(row.estado),
      `${Math.round(row.porcentajeAvance)}%`,
      row.fechaInicio,
      row.fechaCompletado,
      row.puntuacion !== 'N/A' && row.puntuacion !== null && typeof row.puntuacion === 'number' 
        ? Math.round(row.puntuacion) 
        : row.puntuacion
    ]);

    // Crear contenido CSV
    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.map(cell => {
        // Escapar comillas y envolver en comillas si contiene comas o comillas
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `resultados_completos_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calcular paginación
  const totalPages = Math.ceil(filteredResults.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedResults = filteredResults.slice(startIndex, endIndex);

  if (loading) {
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Cargando resultados...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 } }}>
      <Box mb={4} sx={{ width: '100%', minWidth: 0 }}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <TableChartIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              Resultados Completos
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
            disabled={filteredResults.length === 0}
          >
            Descargar CSV
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Selector de empresa */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControl fullWidth>
              <InputLabel>Filtrar por Empresa</InputLabel>
              <Select
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setDepartmentFilter(''); // Limpiar filtro de departamento
                  setOfficeFilter(''); // Limpiar filtro de sede
                }}
                label="Filtrar por Empresa"
              >
                <MenuItem value="">Todas las empresas</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company._id} value={company._id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Filtros */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' }, 
              gap: 2,
              mb: 2
            }}>
              <TextField
                fullWidth
                label="Buscar"
                placeholder="Buscar por nombre, apellido o correo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="">Todos los estados</MenuItem>
                  {uniqueStatuses.map((status) => (
                    <MenuItem key={status} value={status.toLowerCase()}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  label="Departamento"
                >
                  <MenuItem value="">Todos los departamentos</MenuItem>
                  {uniqueDepartments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Sede</InputLabel>
                <Select
                  value={officeFilter}
                  onChange={(e) => setOfficeFilter(e.target.value)}
                  label="Sede"
                >
                  <MenuItem value="">Todas las sedes</MenuItem>
                  {uniqueOffices.map((office) => (
                    <MenuItem key={office} value={office}>
                      {office}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Curso</InputLabel>
                <Select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                  label="Curso"
                >
                  <MenuItem value="">Todos los cursos</MenuItem>
                  {uniqueCourses.map((course) => (
                    <MenuItem key={course} value={course}>
                      {course}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Total de registros: {filteredResults.length}
              </Typography>
              {(statusFilter || departmentFilter || officeFilter || courseFilter) && (
                <Button
                  size="small"
                  onClick={() => {
                    setStatusFilter('');
                    setDepartmentFilter('');
                    setOfficeFilter('');
                    setCourseFilter('');
                  }}
                >
                  Limpiar filtros
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Tabla responsiva: usa el ancho disponible, scroll horizontal solo en barra inferior */}
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: 1400 }, minWidth: 0, mx: 'auto' }}>
        <Card sx={{ width: '100%', minWidth: 0, overflow: 'visible' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, overflow: 'visible', display: 'flex', flexDirection: 'column' }}>
            {/* Espaciador superior (scroll oculto, sincronizado) */}
            <Box
              ref={topScrollRef}
              className="resultados-table-scroll resultados-table-scrollbar-hidden"
              sx={{
                width: '100%',
                minWidth: 0,
                flexShrink: 0,
                overflowX: 'scroll',
                overflowY: 'hidden',
                height: 14,
                mb: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.100',
              }}
            >
              <Box sx={{ minWidth: 1400, height: 1 }} />
            </Box>
            
            <Box
              ref={tableScrollRef}
              className="resultados-table-scroll resultados-table-scrollbar-hidden"
              sx={{
                width: '100%',
                minWidth: 0,
                flex: 1,
                minHeight: 200,
                overflowX: 'scroll',
                overflowY: 'auto',
                maxHeight: { xs: 'calc(100vh - 340px)', sm: 'calc(100vh - 380px)', md: 'calc(100vh - 420px)' },
              }}
            >
              <TableContainer 
                component={Paper} 
                variant="outlined"
                sx={{ 
                  width: 'max-content',
                  minWidth: '100%',
                  '& .MuiTable-root': {
                    minWidth: 1400
                  },
                }}
              >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'nombre'}
                        direction={sortColumn === 'nombre' ? sortDirection : 'asc'}
                        onClick={() => handleSort('nombre')}
                      >
                        <strong>Nombre</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'apellido'}
                        direction={sortColumn === 'apellido' ? sortDirection : 'asc'}
                        onClick={() => handleSort('apellido')}
                      >
                        <strong>Apellido</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'empresa'}
                        direction={sortColumn === 'empresa' ? sortDirection : 'asc'}
                        onClick={() => handleSort('empresa')}
                      >
                        <strong>Empresa</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'sede'}
                        direction={sortColumn === 'sede' ? sortDirection : 'asc'}
                        onClick={() => handleSort('sede')}
                      >
                        <strong>Sede</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'correo'}
                        direction={sortColumn === 'correo' ? sortDirection : 'asc'}
                        onClick={() => handleSort('correo')}
                      >
                        <strong>Correo</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'departamento'}
                        direction={sortColumn === 'departamento' ? sortDirection : 'asc'}
                        onClick={() => handleSort('departamento')}
                      >
                        <strong>Departamento</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'curso'}
                        direction={sortColumn === 'curso' ? sortDirection : 'asc'}
                        onClick={() => handleSort('curso')}
                      >
                        <strong>Curso</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'estado'}
                        direction={sortColumn === 'estado' ? sortDirection : 'asc'}
                        onClick={() => handleSort('estado')}
                      >
                        <strong>Estado</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'porcentajeAvance'}
                        direction={sortColumn === 'porcentajeAvance' ? sortDirection : 'asc'}
                        onClick={() => handleSort('porcentajeAvance')}
                      >
                        <strong>% de Avance</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'fechaInicio'}
                        direction={sortColumn === 'fechaInicio' ? sortDirection : 'asc'}
                        onClick={() => handleSort('fechaInicio')}
                      >
                        <strong>Inicio</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'fechaCompletado'}
                        direction={sortColumn === 'fechaCompletado' ? sortDirection : 'asc'}
                        onClick={() => handleSort('fechaCompletado')}
                      >
                        <strong>Fin</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'puntuacion'}
                        direction={sortColumn === 'puntuacion' ? sortDirection : 'asc'}
                        onClick={() => handleSort('puntuacion')}
                      >
                        <strong>Puntuación</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          {searchTerm ? 'No se encontraron resultados para la búsqueda' : 'No hay resultados disponibles'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedResults.map((row, index) => (
                      <TableRow key={`${row.userId}_${row.courseId}_${index}`} hover>
                        <TableCell>{row.nombre}</TableCell>
                        <TableCell>{row.apellido}</TableCell>
                        <TableCell>{row.empresa}</TableCell>
                        <TableCell>{row.sede}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>{row.correo}</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>{row.departamento}</TableCell>
                        <TableCell>{row.curso}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(row.estado)}
                            color={getStatusColor(row.estado) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minWidth: 100 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <LinearProgress
                                variant="determinate"
                                value={row.porcentajeAvance}
                                sx={{ 
                                  flexGrow: 1,
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: row.porcentajeAvance >= 80 ? 'success.main' : 
                                                    row.porcentajeAvance >= 50 ? 'warning.main' : 'primary.main'
                                  }
                                }}
                              />
                              <Typography variant="body2" sx={{ minWidth: 45, textAlign: 'right' }}>
                                {Math.round(row.porcentajeAvance)}%
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{row.fechaInicio}</TableCell>
                        <TableCell>{row.fechaCompletado}</TableCell>
                        <TableCell>
                          {row.puntuacion !== 'N/A' && row.puntuacion !== null ? (
                            <Chip
                              label={typeof row.puntuacion === 'number' ? Math.round(row.puntuacion) : row.puntuacion}
                              color={typeof row.puntuacion === 'number' && row.puntuacion >= 70 ? 'success' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Ver cursos">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedUser({
                                  userId: row.userId,
                                  userName: `${row.nombre} ${row.apellido}`,
                                  userEmail: row.correo
                                });
                                setUserCourseDetailOpen(true);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </TableContainer>
            </Box>

            {/* Scroll horizontal fijo inferior - siempre visible */}
            <Box
              ref={bottomScrollRef}
              className="resultados-table-scroll"
              sx={{
                width: '100%',
                minWidth: 0,
                flexShrink: 0,
                overflowX: 'scroll',
                overflowY: 'hidden',
                height: 20,
                borderTop: '2px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.100',
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
              }}
            >
              <Box sx={{ minWidth: 1400, height: 1 }} />
            </Box>

            {/* Paginación */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3} flexShrink={0}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </CardContent>
        </Card>
        </Box>

        <UserCourseDetailDialog
          open={userCourseDetailOpen}
          onClose={() => { setUserCourseDetailOpen(false); setSelectedUser(null); }}
          userId={selectedUser?.userId || ''}
          userName={selectedUser?.userName || ''}
          userEmail={selectedUser?.userEmail || ''}
        />
      </Box>
    </Box>
  );
};

export default AdminAllResults;
